import { available, unavailable } from './integer.js';
import type { PoolMathResult } from './types.js';

export interface OutcomeSnapshotIdentity {
  outcomeIndex: number;
  outcomeMint: string;
}

export interface MarketPoolSnapshotIdentity<TOutcome extends OutcomeSnapshotIdentity> {
  market: string;
  outcomes: readonly TOutcome[];
}

export interface ResolveCheckedOutcomeSnapshotInput<TOutcome extends OutcomeSnapshotIdentity> {
  snapshot: MarketPoolSnapshotIdentity<TOutcome>;
  expectedMarket: string;
  expectedOutcomeIndex: number;
  expectedOutcomeMint: string;
}

export interface ResolvedCheckedOutcomeSnapshot<TOutcome extends OutcomeSnapshotIdentity> {
  outcome: TOutcome;
  /** Every holding with this identity consumes the same outcome pool. */
  poolIdentity: string;
  marketIdentity: string;
  marketSnapshotIdentity: string;
  marketSnapshotFingerprint: string;
}

/** Resolves quote state only after binding all position identity fields. */
export function resolveCheckedOutcomeSnapshot<TOutcome extends OutcomeSnapshotIdentity>(
  input: ResolveCheckedOutcomeSnapshotInput<TOutcome>,
): PoolMathResult<ResolvedCheckedOutcomeSnapshot<TOutcome>> {
  if (input.snapshot.market !== input.expectedMarket) {
    return unavailable('snapshot_market_mismatch');
  }
  const outcome = input.snapshot.outcomes.find(
    (candidate) => candidate.outcomeIndex === input.expectedOutcomeIndex,
  );
  if (outcome === undefined) return unavailable('snapshot_outcome_missing');
  if (outcome.outcomeMint !== input.expectedOutcomeMint) {
    return unavailable('snapshot_outcome_mint_mismatch');
  }
  const marketSnapshotFingerprint = jsonSafeSnapshotFingerprint(input.snapshot);
  if (marketSnapshotFingerprint === null) return unavailable('invalid_amount');
  return available({
    outcome,
    poolIdentity: `${input.expectedMarket}:${input.expectedOutcomeIndex}`,
    marketIdentity: input.expectedMarket,
    marketSnapshotIdentity: `${input.expectedMarket}:${marketSnapshotFingerprint}`,
    marketSnapshotFingerprint,
  });
}

function jsonSafeSnapshotFingerprint<TOutcome extends OutcomeSnapshotIdentity>(
  snapshot: MarketPoolSnapshotIdentity<TOutcome>,
): string | null {
  if (!isJsonSafe(snapshot, new Set<object>())) return null;
  const keys = new Set<string>();
  collectJsonObjectKeys(snapshot, keys);
  const fingerprint = JSON.stringify(snapshot, [...keys].sort());
  return fingerprint.length > 0 ? fingerprint : null;
}

function isJsonSafe<TValue>(value: TValue, ancestors: Set<object>): boolean {
  if (value === null) return true;
  if (typeof value === 'string' || typeof value === 'boolean') return true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value !== 'object') return false;
  if (ancestors.has(value)) return false;
  ancestors.add(value);
  if (Array.isArray(value)) {
    const safe = value.every((item) => isJsonSafe(item, ancestors));
    ancestors.delete(value);
    return safe;
  }
  if (Object.getPrototypeOf(value) !== Object.prototype) {
    ancestors.delete(value);
    return false;
  }
  for (const key in value) {
    if (!isJsonSafe(value[key], ancestors)) {
      ancestors.delete(value);
      return false;
    }
  }
  ancestors.delete(value);
  return true;
}

function collectJsonObjectKeys<TValue extends object>(value: TValue, keys: Set<string>): void {
  if (Array.isArray(value)) {
    for (const item of value) {
      if (typeof item === 'object' && item !== null) collectJsonObjectKeys(item, keys);
    }
    return;
  }
  for (const key in value) {
    keys.add(key);
    const child = value[key];
    if (typeof child === 'object' && child !== null) collectJsonObjectKeys(child, keys);
  }
}
